import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, BarChart3, DollarSign, Trophy, Search, Zap, TrendingUp, Users, Video, CheckCircle2, Bot, Radar, LineChart, Shield, Rocket, Star, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import HeroDashboard from "@/components/HeroDashboard";

const BADGES = [
  { icon: Youtube_Icon, label: "Official YouTube API" },
  { icon: Bot, label: "AI Powered" },
  { icon: Radar, label: "Real-Time Analytics" },
  { icon: Search, label: "SEO Insights" },
  { icon: DollarSign, label: "Revenue Estimator" },
  { icon: CheckCircle2, label: "Free Reports" },
];

function Youtube_Icon(props) {
  // Custom simple play mark — avoid using YT's copyrighted logo
  return <Sparkles {...props} />;
}

const FEATURES = [
  { icon: BarChart3, title: "YouTube Channel Statistics", desc: "Subscribers, views, likes, comments and engagement across every video." },
  { icon: DollarSign, title: "YouTube Revenue Calculator", desc: "Realistic earnings estimates based on low, average and high YouTube CPM." },
  { icon: Sparkles, title: "AI YouTube Analytics", desc: "GPT-5.2 strategist highlights strengths, risks and growth opportunities." },
  { icon: Trophy, title: "Rankings & Growth Tracker", desc: "Compare channels side by side and monitor growth over time." },
  { icon: Search, title: "Advanced SEO Analysis", desc: "Title, thumbnail and metadata scoring to boost discoverability." },
  { icon: LineChart, title: "Growth & Engagement Trends", desc: "Interactive charts for views, subscribers and engagement over time." },
  { icon: Rocket, title: "Content Strategy Recommendations", desc: "AI-generated ideas to grow views and subscribers faster." },
  { icon: Shield, title: "Trusted YouTube Data API v3", desc: "All data pulled from the official YouTube API — accurate and up to date." },
];

const STEPS = [
  { icon: Users, step: "01", title: "Paste the URL", desc: "URL, @handle or channel ID — our engine detects it automatically." },
  { icon: BarChart3, step: "02", title: "Instant analysis", desc: "50 videos, full snippet, statistics, categories and growth processed in seconds." },
  { icon: TrendingUp, step: "03", title: "AI recommendations", desc: "Summary, strengths, opportunities, risks and actionable next steps." },
];

const TESTIMONIALS = [
  {
    name: "Alex Morgan",
    role: "YouTube Creator · 1.2M subs",
    quote: "The AI recommendations helped me increase CTR by 18% in two weeks. This is the analytics tool I've been waiting for.",
  },
  {
    name: "Priya Sharma",
    role: "Head of Creator Ops · Bright Media",
    quote: "We use Creator Intelligence Tube to onboard every new creator. The revenue estimator alone saves us hours of manual work.",
  },
  {
    name: "Jordan Lee",
    role: "Brand Partnerships Manager",
    quote: "The channel score gives our team an instant, objective view of any creator we're considering for a partnership.",
  },
];

const FAQS = [
  {
    q: "What is Creator Intelligence Tube?",
    a: "A free YouTube channel analyzer that inspects any public YouTube channel through the official YouTube Data API v3 and interprets the numbers with GPT-5.2. In seconds you get statistics, estimated revenue, growth charts, a channel score and a consultative AI report.",
  },
  {
    q: "How does the YouTube revenue calculator work?",
    a: "We apply market-standard CPM ranges (low $0.50, average $2.50, high $6.00) and the 55% creator share, multiplied by the channel's average views per video and detected upload cadence. Toggle low, mid and high scenarios to see optimistic, realistic and conservative projections.",
  },
  {
    q: "Which YouTube metrics are analyzed?",
    a: "Subscribers, total and per-video views, likes, comments, engagement rate, upload cadence, average duration, top and bottom performing videos, keywords, category, country and more. We also compute a proprietary 0–100 score covering growth, consistency, frequency, engagement, SEO, performance and quality.",
  },
  {
    q: "Does this YouTube channel analyzer work for any channel?",
    a: "Yes. Paste the URL, @handle or channel ID for any public YouTube channel. We fetch the last 50 uploads and every public statistic exposed by YouTube — no login or authorization required.",
  },
  {
    q: "Is Creator Intelligence Tube really free?",
    a: "Yes. All core features — analyze, compare, rankings, revenue estimator and AI diagnosis — are free to use. Cached results respect fair-use API limits.",
  },
];

export default function Home() {
  const [url, setUrl] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e?.preventDefault();
    if (!url.trim()) {
      toast.error("Please paste a YouTube channel URL");
      return;
    }
    navigate("/analyze", { state: { url: url.trim() } });
  };

  const faqJsonLd = {
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="Free YouTube Channel Analyzer with AI — Stats, Revenue & Growth in 30s"
        description="Analyze any YouTube channel for free in 30 seconds. Get real subscribers, views, engagement, estimated revenue, AI diagnosis and growth predictions — powered by GPT-5.2. No signup required."
        path="/"
        keywords="youtube analytics, youtube channel analytics, youtube statistics, youtube channel statistics, youtube channel analyzer, youtube earnings calculator, youtube revenue calculator, youtube revenue estimator, youtube monetization calculator, youtube growth tracker, youtube subscriber tracker, analyze youtube channel, creator analytics, video analytics, youtube insights, youtube performance, channel growth, youtube seo, youtube dashboard, youtube metrics, creator dashboard, audience insights, youtube reports, youtube ai analytics"
        jsonLd={faqJsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-6 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary">
                <Zap className="w-3.5 h-3.5" /> AI-Powered YouTube Analytics Platform
              </div>
              <h1 data-testid="hero-title" className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Discover Everything About{" "}
                <span className="relative inline-block">
                  <span className="relative z-10 text-primary">Any YouTube Channel</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-primary/15 -z-0" />
                </span>
              </h1>
              <p data-testid="hero-subtitle" className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Analyze any public YouTube channel using AI-powered insights, estimated earnings, engagement metrics, growth trends, SEO analysis, audience insights and advanced statistics.
              </p>

              <form onSubmit={submit} data-testid="url-form" className="mt-8 max-w-xl">
                <div className="flex flex-col sm:flex-row gap-2 p-2 rounded-full bg-white dark:bg-card border border-border shadow-lg shadow-black/[0.03]">
                  <div className="flex-1 flex items-center gap-2 px-4">
                    <Search className="w-5 h-5 text-muted-foreground shrink-0" aria-hidden="true" />
                    <label htmlFor="channel-url" className="sr-only">YouTube channel URL</label>
                    <Input
                      id="channel-url"
                      data-testid="url-input"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Paste a YouTube URL or type a channel name"
                      className="border-0 shadow-none focus-visible:ring-0 text-base h-11 bg-transparent px-0"
                    />
                  </div>
                  <Button
                    data-testid="analyze-button"
                    type="submit"
                    className="btn-primary h-11 px-6"
                  >
                    Analyze Channel <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>

              <div className="mt-6 flex flex-wrap gap-2">
                {BADGES.map((b) => (
                  <div key={b.label} className="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-card border border-border/70 px-3 py-1.5 text-xs font-medium text-foreground/80 shadow-sm">
                    <b.icon className="w-3.5 h-3.5 text-primary" />
                    {b.label}
                  </div>
                ))}
              </div>

              <div className="mt-6 text-xs font-mono text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                <span>Try:</span>
                <button type="button" className="hover:text-primary" onClick={() => setUrl("https://www.youtube.com/@MrBeast")}>@MrBeast</button>
                <button type="button" className="hover:text-primary" onClick={() => setUrl("https://www.youtube.com/@mkbhd")}>@mkbhd</button>
                <button type="button" className="hover:text-primary" onClick={() => setUrl("https://www.youtube.com/@veritasium")}>@veritasium</button>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <HeroDashboard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center overline mb-6">Trusted by creators, brands and agencies worldwide</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
            {["CreatorHub", "Bright Media", "PixelWave", "TubeIQ", "StudioNine", "GrowLab"].map((n) => (
              <div key={n} className="text-lg font-extrabold tracking-tight text-muted-foreground">{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-3xl">
          <div className="overline">Features</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
            Everything you need to grow a YouTube channel
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            A single dashboard combining statistics, AI diagnosis, revenue estimation and growth tracking — built for modern creators.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.article
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.05 * i, duration: 0.4 }}
              className="card-flat card-hover p-6"
              data-testid={`feature-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-base">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 border-t border-border/60">
        <div className="max-w-3xl">
          <div className="overline">How it works</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
            From URL to insights in three steps
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.step} className="card-flat p-8 relative overflow-hidden">
              <div className="absolute top-6 right-6 text-7xl font-extrabold text-primary/[0.06] leading-none pointer-events-none">
                {s.step}
              </div>
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center relative">
                <s.icon className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="mt-6 font-bold text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 border-t border-border/60">
        <div className="max-w-3xl">
          <div className="overline">Testimonials</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Loved by creators and teams</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card-flat p-6">
              <Quote className="w-8 h-8 text-primary/40" />
              <p className="mt-4 text-base leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white font-bold text-sm">
                  {t.name.split(" ").map((s) => s[0]).join("")}
                </div>
                <div>
                  <div className="font-bold text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
              <div className="mt-4 flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 border-t border-border/60">
        <div className="text-center">
          <div className="overline">FAQ</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Frequently asked questions</h2>
        </div>
        <div className="mt-12 space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="card-flat p-6 group">
              <summary className="cursor-pointer font-bold text-base list-none flex items-start justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary/80 p-12 sm:p-16 text-center">
          <div className="absolute inset-0 opacity-20 grid-pattern" aria-hidden="true" />
          <div className="relative">
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Ready to analyze your next channel?
            </h2>
            <p className="mt-4 text-white/90 max-w-2xl mx-auto">
              Get statistics, revenue estimates, growth charts and AI-powered recommendations for any public YouTube channel — in seconds.
            </p>
            <Button
              onClick={() => navigate("/analyze")}
              className="mt-8 h-12 px-8 rounded-full bg-white text-primary hover:bg-white/95 font-bold text-base shadow-xl"
              data-testid="cta-bottom"
            >
              Analyze a Channel Now <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
